/**
 * Keeps the essay spelling/grammar tooltips inside the viewport.
 *
 * These tooltips are cooltipz-style CSS pseudo-elements (`.essay[...]:after`).
 * cooltipz anchors the tooltip at `left: 50%` of the highlighted word and gives
 * it a fixed width (e.g. 300px for `data-cooltipz-size="large"`). The winning
 * horizontal transform is set in src/assets/css/designer.css
 * (`.essay.bl-bg:after` / `.rd-bg` / `.or-bg`) — previously a static
 * `translate(-25%)`, which does NOT adapt to the viewport, so a highlighted word
 * near the right (or left) edge had its tooltip clipped by the window.
 *
 * Because the tooltip is a pseudo-element, JS can't position it directly. Instead
 * the designer.css rules now use `translateX(var(--tt-tx, -50%))`, and this module
 * measures the hovered word on `mouseover` and sets `--tt-tx` (in px) so the
 * tooltip is centered when there's room and only nudged just enough to stay
 * on-screen near an edge.
 *
 * The `.essay` markup is backend-generated HTML rendered via ReactHtmlParser in
 * several places (Practice, Result, History, WritingResult, …), so a single
 * document-level delegated listener installed at boot covers them all.
 */
export function installEssayTooltipPositioner() {
  if (typeof document === "undefined") return;

  const MARGIN = 8; // min px between the tooltip and the viewport edge

  document.addEventListener(
    "mouseover",
    (e) => {
      const el =
        e.target && typeof e.target.closest === "function"
          ? e.target.closest(".essay")
          : null;
      if (!el) return;

      const rect = el.getBoundingClientRect();
      let width = parseFloat(getComputedStyle(el, "::after").width);
      if (!width || Number.isNaN(width)) width = 300; // cooltipz "large" fallback

      const viewportWidth = document.documentElement.clientWidth;
      // The tooltip's ::after box is anchored at left:50% of the word, then
      // shifted by `tx` px. So: boxLeft = anchorX + tx, boxRight = anchorX + tx + width.
      const anchorX = rect.left + rect.width / 2;

      const centeredTx = -width / 2; // default: centered over the word
      const minTx = MARGIN - anchorX; // keep left edge on-screen
      const maxTx = viewportWidth - MARGIN - width - anchorX; // keep right edge on-screen

      // Start centered, then clamp within [minTx, maxTx]. When the word is very
      // close to an edge the range can invert; minTx (left edge) wins so text
      // isn't lost off the left.
      let tx = Math.min(Math.max(centeredTx, minTx), maxTx);
      if (tx < minTx) tx = minTx;

      el.style.setProperty("--tt-tx", `${Math.round(tx)}px`);
    },
    true
  );
}
